import { useState } from 'react'
import { useDebounce } from 'react-use'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { cn } from '~/@/lib/utils'
import { useGetIconsQuery, useSearchIconsLazyQuery } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DossIcon } from '~/components/DossIcon'

export interface PickableIcon {
  id: string
  name: string
}

interface IconPickerGridProps {
  /** Focus the search on mount. Off by default, since a host may have its own field to focus first */
  autoFocusSearch?: boolean
  selectedIconName?: string | null
  onSelect: (icon: PickableIcon) => void
  columns?: number
  className?: string
  gridClassName?: string
}

const SEARCH_DEBOUNCE_MS = 200
/** Shared so previews elsewhere can show an icon at the same size the picker does */
export const ICON_PICKER_ICON_SIZE = 18

/**
 * Searchable icon grid over the global icon catalog. The search field stays fixed above the scrolling
 * grid, so long lists never push it out of view.
 */
export const IconPickerGrid = ({
  autoFocusSearch = false,
  selectedIconName,
  onSelect,
  columns = 6,
  className,
  gridClassName,
}: IconPickerGridProps) => {
  const { t } = useTypedTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredIcons, setFilteredIcons] = useState<PickableIcon[]>([])

  const { data: allIcons } = useGetIconsQuery({
    onCompleted(data) {
      setFilteredIcons(data.icons)
    },
  })

  const [searchIcons] = useSearchIconsLazyQuery({
    onCompleted: (data) => {
      setFilteredIcons(data.searchIcons)
    },
  })

  useDebounce(
    () => {
      if (searchTerm !== '') {
        searchIcons({ variables: { input: searchTerm } })
      } else if (allIcons) {
        setFilteredIcons(allIcons.icons)
      }
    },
    SEARCH_DEBOUNCE_MS,
    [searchTerm],
  )

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <ShortTextInput
        autoFocus={autoFocusSearch}
        onChange={(event) => setSearchTerm(event.target.value)}
        onKeyDown={(event) => event.stopPropagation()}
        placeholder={t('common.search_placeholder')}
        type="text"
        value={searchTerm}
        // InputWrapper pads itself, which would inset the field from the grid below it
        wrapperClassName="p-0"
      />

      <div
        className={cn('grid place-items-center gap-1 overflow-y-auto py-1', gridClassName)}
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {filteredIcons.length > 0 ? (
          filteredIcons.map((icon) => (
            <button
              key={icon.id}
              className={cn(
                'flex size-8 cursor-pointer items-center justify-center rounded-sm p-1 hover:bg-gray-2 focus-visible:outline-none',
                selectedIconName === icon.name && 'bg-blue-2 ring-1 ring-inset ring-blue-8',
              )}
              onClick={() => onSelect(icon)}
              type="button"
            >
              <DossIcon name={icon.name} size={ICON_PICKER_ICON_SIZE} />
            </button>
          ))
        ) : (
          <div className="col-span-full text-center">{t('table.no_icons_found')}</div>
        )}
      </div>
    </div>
  )
}
